import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { api, errMsg, waLink, fmtDate } from "../lib/api";

const EMPTY = { first_name: "", last_name: "", phone: "", email: "", course_id: "", event_id: "", contact_time: "", note: "", kvkk_ack: false, contact_consent: false, website: "" };

export default function ApplyForm({ courseId = "", compact = false }) {
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const [f, setF] = useState({ ...EMPTY, course_id: courseId });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const started = useRef(Date.now());

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data));
    api.get("/events", { params: { open_only: true } }).then((r) => setEvents(r.data));
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const periods = events.filter((e) => e.course_id === f.course_id);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.kvkk_ack) return setError("Devam etmek için aydınlatma metnini okuduğunuzu onaylayın.");
    if (!/^[0-9 +()-]{10,20}$/.test(f.phone)) return setError("Geçerli bir telefon numarası girin (ör. 0505 123 45 67).");
    setBusy(true);
    try {
      const { data } = await api.post("/applications", { ...f, started_at: started.current });
      setDone(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return (
    <div className="border-2 border-green-600 bg-green-50 p-6 sm:p-8" data-testid="apply-success">
      <CheckCircle2 className="text-green-700" size={36} />
      <h3 className="mt-3 text-xl font-black">Başvurunuz alındı</h3>
      <p className="mt-2 text-sm text-zinc-700">Başvuru numaranız: <strong className="text-lg font-display" data-testid="apply-success-number">{done.application_no}</strong></p>
      <p className="mt-2 text-sm text-zinc-700">{done.email_sent ? "Başvuru bilgileriniz e-posta adresinize gönderildi." : "Başvurunuz kaydedildi; onay e-postası şu anda gönderilemedi, kurumumuz sizinle telefonla iletişime geçecektir."} Durumunu başvuru numaranız ve e-postanızla takip edebilirsiniz.</p>
      <div className="mt-5 flex flex-wrap gap-2">
        <Link to="/kurs-basvurusu#takip" className="btn-dark" data-testid="apply-track-link">Başvurumu Takip Et</Link>
        <button className="btn-red" onClick={() => { setDone(null); setF({ ...EMPTY }); started.current = Date.now(); }} data-testid="apply-new-btn">Yeni Başvuru</button>
      </div>
    </div>
  );

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate data-testid="apply-form">
      <div><label className="label" htmlFor="af-fn">Ad *</label><input id="af-fn" className="field" required minLength={2} value={f.first_name} onChange={set("first_name")} data-testid="apply-first-name" /></div>
      <div><label className="label" htmlFor="af-ln">Soyad *</label><input id="af-ln" className="field" required minLength={2} value={f.last_name} onChange={set("last_name")} data-testid="apply-last-name" /></div>
      <div><label className="label" htmlFor="af-ph">Telefon *</label><input id="af-ph" className="field" type="tel" inputMode="tel" placeholder="05xx xxx xx xx" required value={f.phone} onChange={set("phone")} data-testid="apply-phone" /></div>
      <div><label className="label" htmlFor="af-em">E-posta *</label><input id="af-em" className="field" type="email" required value={f.email} onChange={set("email")} data-testid="apply-email" /></div>
      <div className={compact ? "sm:col-span-2" : ""}>
        <label className="label" htmlFor="af-c">İlgilenilen eğitim *</label>
        <select id="af-c" className="field" required value={f.course_id} onChange={(e) => setF({ ...f, course_id: e.target.value, event_id: "" })} data-testid="apply-course">
          <option value="">Seçiniz</option>
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
      </div>
      <div className={compact ? "sm:col-span-2" : ""}>
        <label className="label" htmlFor="af-p">Tercih edilen kurs dönemi</label>
        <select id="af-p" className="field" value={f.event_id} onChange={set("event_id")} data-testid="apply-period">
          <option value="">{periods.length ? "Fark etmez / sonra belirleyelim" : "Açık dönem yok – bilgi talebi olarak ilet"}</option>
          {periods.map((p) => <option key={p.id} value={p.id}>{p.title} – {fmtDate(p.start_date)}{p.date_confirmed ? "" : " (tarih kesinleşmedi)"}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className="label" htmlFor="af-t">Uygun iletişim zamanı</label>
        <select id="af-t" className="field" value={f.contact_time} onChange={set("contact_time")} data-testid="apply-contact-time">
          <option value="">Fark etmez</option><option>Hafta içi 09:00 – 12:00</option><option>Hafta içi 12:00 – 15:00</option><option>Hafta içi 15:00 – 18:00</option><option>Hafta sonu</option>
        </select>
      </div>
      <div className="sm:col-span-2"><label className="label" htmlFor="af-n">Açıklama (isteğe bağlı)</label><textarea id="af-n" rows={3} maxLength={1000} className="field" value={f.note} onChange={set("note")} data-testid="apply-note" /></div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={set("website")} aria-hidden="true" />
      <div className="sm:col-span-2 space-y-3 text-sm">
        <label className="flex gap-3 items-start"><input type="checkbox" className="mt-1 accent-[#dc2626] w-4 h-4" checked={f.kvkk_ack} onChange={set("kvkk_ack")} data-testid="apply-kvkk" />
          <span><Link to="/yasal/kvkk-aydinlatma-metni" target="_blank" className="underline font-semibold">KVKK Aydınlatma Metni</Link>'ni okudum; başvurum kapsamında kişisel verilerimin işlenmesine ilişkin bilgilendirildim. *</span></label>
        <label className="flex gap-3 items-start"><input type="checkbox" className="mt-1 accent-[#dc2626] w-4 h-4" checked={f.contact_consent} onChange={set("contact_consent")} data-testid="apply-contact-consent" />
          <span>Kurs dönemleri ve duyurular hakkında tarafıma e-posta/telefon ile bilgilendirme yapılmasına izin veriyorum. (isteğe bağlı)</span></label>
        <p className="text-xs text-zinc-500">Bu aşamada kimlik numarası, sağlık raporu veya belge istemiyoruz.</p>
      </div>
      {error && <p className="sm:col-span-2 border-l-4 border-[#dc2626] bg-red-50 text-red-800 px-4 py-3 text-sm" role="alert" data-testid="apply-error">{error}</p>}
      <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3">
        <button type="submit" className="btn-red flex-1" disabled={busy} data-testid="apply-submit-btn">{busy && <Loader2 size={16} className="animate-spin" />}Başvuruyu Gönder</button>
        <a href={waLink("Merhaba, kurs başvurusu hakkında bilgi almak istiyorum.")} target="_blank" rel="noreferrer" className="btn-outline text-zinc-900 hover:!bg-zinc-950 hover:!text-white" data-testid="apply-whatsapp-btn"><MessageCircle size={16} />WhatsApp ile Sor</a>
      </div>
    </form>
  );
}
